const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API_URL = `${API_BASE_URL}/api`;

class ApiService {
  constructor() {
    this.baseURL = API_URL;
    // Log API configuration in development
    if (process.env.NODE_ENV === 'development') {
      console.log('API Configuration:', {
        REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
        API_BASE_URL,
        API_URL: this.baseURL
      });
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Remove Content-Type for FormData
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    console.log('API Request:', {
      url,
      method: config.method || 'GET',
      headers: config.headers
    });

    try {
      const response = await fetch(url, config);
      
      // Log response status for debugging
      console.log('API Response Status:', response.status);
      
      if (!response.ok) {
        // Try to get error details from response
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
        }
        
        console.error('API Error Response:', errorData);
        throw new Error(errorData.message || `API request failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log('API Success Response:', data);
      return data;
    } catch (error) {
      console.error('API Error:', {
        message: error.message,
        url,
        stack: error.stack
      });
      throw error;
    }
  }

  // Contact form submission
  async submitContact(formData) {
    console.log('Submitting contact form:', formData);
    return await this.request('/contact', {
      method: 'POST',
      body: formData
    });
  }

  // Client intake form submission
  async submitClientIntakeForm(formData) {
    console.log('Submitting client intake form:', formData);
    
    // Validate required fields before submission
    const requiredFields = ['fullName', 'companyName', 'industry', 'businessSize', 'email', 'businessPhase', 'strategicGoals', 'startDate', 'successDefinition'];
    const missingFields = requiredFields.filter(field => !formData[field] || formData[field].trim() === '');
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // Validate fullName format (backend has strict regex)
    const nameRegex = /^[a-zA-Z\s\-\.\']+$/;
    if (!nameRegex.test(formData.fullName.trim())) {
      throw new Error('Full name can only contain letters, spaces, hyphens, dots, and apostrophes');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      throw new Error('Please provide a valid email address');
    }

    // Ensure arrays are properly formatted and filter empty values
    const processedData = {
      ...formData,
      servicesNeeded: Array.isArray(formData.servicesNeeded) ? formData.servicesNeeded.filter(s => s.trim()) : [],
      valueAddInterests: Array.isArray(formData.valueAddInterests) ? formData.valueAddInterests.filter(s => s.trim()) : [],
      // Ensure all string fields are properly trimmed
      fullName: formData.fullName?.trim(),
      companyName: formData.companyName?.trim(),
      email: formData.email?.trim().toLowerCase(),
      strategicGoals: formData.strategicGoals?.trim(),
      successDefinition: formData.successDefinition?.trim(),
      currentTools: formData.currentTools?.trim() || '',
      preferredTech: formData.preferredTech?.trim() || '',
      integrationSystems: formData.integrationSystems?.trim() || '',
      launchDate: formData.launchDate?.trim() || '',
      keyKPIs: formData.keyKPIs?.trim() || '',
      phone: formData.phone?.trim() || '',
      preferredContact: formData.preferredContact || 'Email',
      // Add metadata fields
      source: 'website',
      userAgent: navigator.userAgent
    };

    // Remove any undefined or null values
    Object.keys(processedData).forEach(key => {
      if (processedData[key] === undefined || processedData[key] === null) {
        delete processedData[key];
      }
    });

    // Validate enum values to match backend exactly
    const validIndustries = ['Technology', 'Healthcare', 'Financial Services', 'Life Sciences', 'Manufacturing', 'Retail/E-commerce', 'Education', 'Government', 'Other'];
    const validBusinessSizes = ['Startup (1-10 employees)', 'Small Business (11-50 employees)', 'Medium Business (51-250 employees)', 'Large Enterprise (250+ employees)'];
    const validBusinessPhases = ['Ideation', 'Market FIT Analysis', 'Minimum Viable Product (MVP)', 'Prototype/Mock Ups POC', 'Implementation', 'Scale up'];
    const validStartDates = ['Immediately', '1–2 months', '3–6 months', 'Flexible'];
    const validContactMethods = ['Email', 'Phone', 'Video Call', 'In-Person Meeting'];

    // Validate required enum fields
    if (!validIndustries.includes(processedData.industry)) {
      throw new Error(`Invalid industry value: ${processedData.industry}`);
    }
    if (!validBusinessSizes.includes(processedData.businessSize)) {
      throw new Error(`Invalid business size value: ${processedData.businessSize}`);
    }
    if (!validBusinessPhases.includes(processedData.businessPhase)) {
      throw new Error(`Invalid business phase value: ${processedData.businessPhase}`);
    }
    if (!validStartDates.includes(processedData.startDate)) {
      throw new Error(`Invalid start date value: ${processedData.startDate}`);
    }
    if (processedData.preferredContact && !validContactMethods.includes(processedData.preferredContact)) {
      throw new Error(`Invalid preferred contact value: ${processedData.preferredContact}`);
    }

    // Validate string length requirements (matching backend validation)
    if (processedData.fullName.length < 2 || processedData.fullName.length > 100) {
      throw new Error('Full name must be between 2 and 100 characters');
    }
    if (processedData.companyName.length < 2 || processedData.companyName.length > 200) {
      throw new Error('Company name must be between 2 and 200 characters');
    }
    if (processedData.strategicGoals.length < 10 || processedData.strategicGoals.length > 2000) {
      throw new Error('Strategic goals must be between 10 and 2000 characters');
    }
    if (processedData.successDefinition.length < 10 || processedData.successDefinition.length > 2000) {
      throw new Error('Success definition must be between 10 and 2000 characters');
    }

    // Validate optional field lengths
    if (processedData.currentTools && processedData.currentTools.length > 2000) {
      throw new Error('Current tools description cannot exceed 2000 characters');
    }
    if (processedData.preferredTech && processedData.preferredTech.length > 2000) {
      throw new Error('Preferred tech description cannot exceed 2000 characters');
    }
    if (processedData.integrationSystems && processedData.integrationSystems.length > 2000) {
      throw new Error('Integration systems description cannot exceed 2000 characters');
    }
    if (processedData.launchDate && processedData.launchDate.length > 500) {
      throw new Error('Launch date cannot exceed 500 characters');
    }
    if (processedData.keyKPIs && processedData.keyKPIs.length > 2000) {
      throw new Error('Key KPIs cannot exceed 2000 characters');
    }

    console.log('Processed and validated form data:', processedData);

    return await this.request('/clientIntake', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(processedData)
    });
  }

  // Newsletter subscription
  async subscribeNewsletter(data) {
    return this.request('/subscribers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Blog APIs
  async getBlogPosts(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/content/public/blog?${query}`);
  }

  async getBlogPost(slug) {
    return this.request(`/content/public/blog/${slug}`);
  }

  // Analytics APIs
  async trackPageView(data) {
    try {
      // Generate or get session ID
      let sessionId = localStorage.getItem('analytics_session_id');
      if (!sessionId) {
        sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('analytics_session_id', sessionId);
        localStorage.setItem('session_start_time', Date.now().toString());
      }

      // Check if session is older than 30 minutes, then create new session
      const sessionStartTime = localStorage.getItem('session_start_time');
      if (sessionStartTime && (Date.now() - parseInt(sessionStartTime)) > 30 * 60 * 1000) {
        sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('analytics_session_id', sessionId);
        localStorage.setItem('session_start_time', Date.now().toString());
      }

      return await this.request('/analytics/track', {
        method: 'POST',
        body: JSON.stringify({
          type: 'pageview',
          page: data.page || window.location.pathname,
          device: this.getDeviceType(),
          source: this.getTrafficSource(),
          userAgent: navigator.userAgent,
          sessionId: sessionId,
          ip: data.ip,
          ...data
        }),
      });
    } catch (error) {
      // Fail silently for analytics - don't throw errors
      console.log('Analytics tracking failed:', error.message);
      return { status: 'error', message: 'Analytics tracking failed' };
    }
  }

  async trackEvent(data) {
    try {
      return await this.request('/analytics/track', {
        method: 'POST',
        body: JSON.stringify({
          type: 'event',
          event: data.event,
          page: data.page || window.location.pathname,
          device: this.getDeviceType(),
          source: this.getTrafficSource(),
          userAgent: navigator.userAgent,
          ...data
        }),
      });
    } catch (error) {
      // Fail silently for analytics - don't throw errors
      console.log('Analytics tracking failed:', error.message);
      return { status: 'error', message: 'Analytics tracking failed' };
    }
  }

  // Helper methods for analytics
  getDeviceType() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/tablet|ipad|playbook|silk/.test(userAgent)) {
      return 'tablet';
    } else if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/.test(userAgent)) {
      return 'mobile';
    } else {
      return 'desktop';
    }
  }

  getTrafficSource() {
    const referrer = document.referrer;
    if (!referrer) return 'direct';
    
    const hostname = new URL(referrer).hostname;
    
    if (hostname.includes('google')) return 'organic';
    if (hostname.includes('facebook') || hostname.includes('twitter') || hostname.includes('linkedin')) return 'social';
    if (hostname === window.location.hostname) return 'internal';
    
    return 'referral';
  }

  // Health check
  async getHealth() {
    return this.request('/health');
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;

// Export specific methods for convenience
export const {
  submitContact,
  submitClientIntakeForm,
  subscribeNewsletter,
  getBlogPosts,
  getBlogPost,
  trackPageView,
  trackEvent,
  getHealth
} = apiService; 